import { Button, ButtonDescription, Visible, Hidden, Link, Typography, Box } from "@vp/swan";
import React, { FunctionComponent } from "react";

interface CtaButtonProps {
  linkProperties?: { href: string; openInNewTab: boolean };
  onClickHandler?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  isCollapsedOnMobile?: boolean;
  disabled: boolean;
  buttonKey: string;
  text: string;
  description?: string;
  iconComponent?: React.ReactNode;
  textButtonProps?: {
    skin: "primary" | "secondary";
  };
  addBottomSpace: boolean;
  pageSection?: string;
  position?: number;
}

export const CtaButton: FunctionComponent<CtaButtonProps> = (props: CtaButtonProps) => {
  const {
    onClickHandler,
    isCollapsedOnMobile,
    disabled,
    buttonKey,
    text,
    description,
    iconComponent,
    pageSection,
    position,
    linkProperties,
    addBottomSpace,
  } = props;

  const commonClickableProps = {
    href: !disabled ? linkProperties?.href : undefined,
    target: linkProperties?.openInNewTab ? "_blank" : undefined,
    rel: linkProperties?.openInNewTab ? "noopener" : undefined,
    onClick: onClickHandler,
    key: `cta-${buttonKey}`,
    disabled,
  };

  const normalizedTextButtonProps = {
    ...props.textButtonProps,
    ...commonClickableProps,
    component: (linkProperties && !disabled ? "a" : "button") as any,
  };

  const InnerButton = (
    <Button
      {...normalizedTextButtonProps}
      specialVariant="design-path"
      width="full-width"
      iconPosition="right"
      data-section={pageSection}
      data-position={position}
      data-translation={buttonKey}
    >
      {text}
      {iconComponent}
      {!!description && (
        <ButtonDescription className="cta-description">{description}</ButtonDescription>
      )}
    </Button>
  );

  const InnerLink = (
    <Typography textAlign="center">
      <Link withIconSeparated fontSize={"xsmall"} {...commonClickableProps}>
        {iconComponent}
        <Typography fontWeight="bold">{text}</Typography>
      </Link>
    </Typography>
  );

  if (isCollapsedOnMobile) {
    return (
      <Box
        marginBottom={addBottomSpace ? "between-actions" : "0"}
        key={`cta-container-${buttonKey}`}
      >
        <Hidden xs>{InnerButton}</Hidden>
        <Visible xs>{InnerLink}</Visible>
      </Box>
    );
  }

  return (
    <Box marginBottom={addBottomSpace ? "between-actions" : "0"} key={`cta-container-${buttonKey}`}>
      {InnerButton}
    </Box>
  );
};
